const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const Cast = require('../../util/cast');

const LabelBlock = BlockType.LABEL || 'label';

/*
 * For local testing:
 * First upload madbot-ai-tutor.php to your server.
 * Then keep this full URL.
 *
 * Later, after final deployment on studio.madbot.in,
 * you may change it to:
 * const MADBOT_AI_API_URL = '/api/madbot-ai-tutor.php';
 */
const MADBOT_AI_API_URL = 'https://studio.madbot.in/api/madbot-ai-tutor.php';

class Scratch3MadBotAITutor {
    constructor (runtime) {
        this.runtime = runtime;

        this.language = 'English';
        this.role = 'Friendly Teacher';
        this.length = 'Short';

        this.answer = '';
        this.status = 'Ready';
        this.busy = false;

        this.history = [];
    }

    getInfo () {
        return {
            id: 'madbotAITutor',
            name: 'MadBot AI Tutor',
            color1: '#F88A17',
            color2: '#D96F00',
            color3: '#8A3F00',

            blocks: [
                {
                    blockType: LabelBlock,
                    text: 'AI Settings'
                },
                {
                    opcode: 'setLanguage',
                    blockType: BlockType.COMMAND,
                    text: 'set AI language to [LANGUAGE]',
                    arguments: {
                        LANGUAGE: {
                            type: ArgumentType.STRING,
                            menu: 'languages',
                            defaultValue: 'English'
                        }
                    }
                },
                {
                    opcode: 'setRole',
                    blockType: BlockType.COMMAND,
                    text: 'set AI role to [ROLE]',
                    arguments: {
                        ROLE: {
                            type: ArgumentType.STRING,
                            menu: 'roles',
                            defaultValue: 'Friendly Teacher'
                        }
                    }
                },
                {
                    opcode: 'setLength',
                    blockType: BlockType.COMMAND,
                    text: 'set answer length to [LENGTH]',
                    arguments: {
                        LENGTH: {
                            type: ArgumentType.STRING,
                            menu: 'lengths',
                            defaultValue: 'Short'
                        }
                    }
                },

                {
                    blockType: LabelBlock,
                    text: 'Ask AI'
                },
                {
                    opcode: 'askAI',
                    blockType: BlockType.COMMAND,
                    text: 'ask AI [QUESTION] and wait',
                    arguments: {
                        QUESTION: {
                            type: ArgumentType.STRING,
                            defaultValue: 'What is robotics?'
                        }
                    }
                },
                {
                    opcode: 'clearChat',
                    blockType: BlockType.COMMAND,
                    text: 'clear AI chat'
                },

                {
                    blockType: LabelBlock,
                    text: 'AI Result'
                },
                {
                    opcode: 'getAnswer',
                    blockType: BlockType.REPORTER,
                    text: 'AI answer'
                },
                {
                    opcode: 'getStatus',
                    blockType: BlockType.REPORTER,
                    text: 'AI status'
                },
                {
                    opcode: 'isBusy',
                    blockType: BlockType.BOOLEAN,
                    text: 'AI is thinking?'
                },
                {
                    opcode: 'getLanguage',
                    blockType: BlockType.REPORTER,
                    text: 'AI language'
                },
                {
                    opcode: 'getRole',
                    blockType: BlockType.REPORTER,
                    text: 'AI role'
                }
            ],

            menus: {
                languages: {
                    acceptReporters: true,
                    items: [
                        'English',
                        'Hindi',
                        'Gujarati'
                    ]
                },
                roles: {
                    acceptReporters: true,
                    items: [
                        'Friendly Teacher',
                        'Coding Helper',
                        'Robotics Helper',
                        'Quiz Maker',
                        'Story Writer'
                    ]
                },
                lengths: {
                    acceptReporters: true,
                    items: [
                        'Short',
                        'Medium',
                        'Detailed'
                    ]
                }
            }
        };
    }

    setLanguage (args) {
        const value = Cast.toString(args.LANGUAGE);

        if (['English', 'Hindi', 'Gujarati'].includes(value)) {
            this.language = value;
        }
    }

    setRole (args) {
        const value = Cast.toString(args.ROLE);

        if ([
            'Friendly Teacher',
            'Coding Helper',
            'Robotics Helper',
            'Quiz Maker',
            'Story Writer'
        ].includes(value)) {
            this.role = value;
        }
    }

    setLength (args) {
        const value = Cast.toString(args.LENGTH);

        if (['Short', 'Medium', 'Detailed'].includes(value)) {
            this.length = value;
        }
    }

    async askAI (args) {
        const question = Cast.toString(args.QUESTION).trim();

        if (!question) {
            this.answer = 'Please type a question.';
            this.status = 'Question is empty';
            return;
        }

        if (this.busy) {
            this.status = 'AI is already thinking';
            return;
        }

        this.busy = true;
        this.status = 'Thinking...';

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 60000);

            const payload = {
                question: question,
                language: this.language,
                role: this.role,
                length: this.length,
                history: this.history.slice(-8)
            };

            const response = await fetch(MADBOT_AI_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            const responseText = await response.text();

            let data = null;

            try {
                data = JSON.parse(responseText);
            } catch (error) {
                throw new Error('Server did not return valid JSON.');
            }

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'AI server error.');
            }

            this.answer = data.answer || 'No answer received.';
            this.status = 'Ready';

            this.history.push({
                role: 'user',
                content: question
            });

            this.history.push({
                role: 'assistant',
                content: this.answer
            });

            if (this.history.length > 12) {
                this.history = this.history.slice(-12);
            }
        } catch (error) {
            if (error && error.name === 'AbortError') {
                this.answer = 'AI request timed out. Please try again.';
                this.status = 'Timeout';
            } else {
                this.answer = 'AI error: ' + (error.message || error);
                this.status = 'Error';
            }
        } finally {
            this.busy = false;
        }
    }

    clearChat () {
        this.answer = '';
        this.status = 'Ready';
        this.history = [];
    }

    getAnswer () {
        return this.answer;
    }

    getStatus () {
        return this.status;
    }

    isBusy () {
        return this.busy;
    }

    getLanguage () {
        return this.language;
    }

    getRole () {
        return this.role;
    }
}

module.exports = Scratch3MadBotAITutor;